import type { TRPCLink } from '@trpc/client'
import type { AnyTRPCRouter } from '@trpc/server'
import { observable } from '@trpc/server/observable'
import type {
	TrpcDebugEvent,
	TrpcDebugListener,
	TrpcOperationRecord,
} from './types'

const listeners = new Set<TrpcDebugListener>()

// tRPC op ids restart at 1 on every page load, and the desktop dock keeps
// operations across app reloads, so keys carry a per-load prefix.
const pageId = Math.random().toString(36).slice(2, 8)

/** Receive start/end events for every operation passing through a debug link. */
export function subscribeTrpcDebug(listener: TrpcDebugListener): () => void {
	listeners.add(listener)
	return () => {
		listeners.delete(listener)
	}
}

function emit(event: TrpcDebugEvent): void {
	for (const listener of listeners) {
		// A broken listener must never break the request it is observing.
		try {
			listener(event)
		} catch (error) {
			console.warn('[tRPC debug] listener threw', error)
		}
	}
}

/**
 * Observe-only link: records each operation's lifecycle and forwards every
 * value, error, and completion from the next link untouched. Place it before
 * the terminating transport link. With no listeners subscribed it steps
 * aside entirely, so it can stay installed in production.
 */
export function createTrpcDebugLink<TRouter extends AnyTRPCRouter>({
	client,
}: {
	client: string
}): TRPCLink<TRouter> {
	return () =>
		({ op, next }) => {
			if (listeners.size === 0) return next(op)

			return observable((observer) => {
				const startedAtPerf = performance.now()
				const record: TrpcOperationRecord = {
					key: `${pageId}:${client}#${op.id}`,
					id: op.id,
					client,
					path: op.path,
					type: op.type,
					input: op.input,
					status: 'pending',
					startedAt: Date.now(),
				}
				emit({ kind: 'start', record })

				let settled = false
				function settle(patch: Partial<TrpcOperationRecord>): void {
					if (settled) return
					settled = true
					emit({
						kind: 'end',
						record: {
							...record,
							...patch,
							endedAt: Date.now(),
							durationMs: performance.now() - startedAtPerf,
						},
					})
				}

				const subscription = next(op).subscribe({
					next(envelope) {
						// Settle before forwarding so the record reflects the answer
						// even if a consumer unsubscribes synchronously on this value.
						if (op.type !== 'subscription' && 'data' in envelope.result) {
							settle({ status: 'success', result: envelope.result.data })
						}
						observer.next(envelope)
					},
					error(error) {
						settle(
							op.signal?.aborted
								? { status: 'cancelled' }
								: { status: 'error', error },
						)
						observer.error(error)
					},
					complete() {
						settle({ status: 'success' })
						observer.complete()
					},
				})

				return () => {
					settle({ status: 'cancelled' })
					subscription.unsubscribe()
				}
			})
		}
}
